"use client";
/* 🔑 공용 원자 — 두 스킨(`_skins/*`)이 같이 씁니다. 스킨 고유의 모양은 여기 두지 않습니다 (`docs/base/11` §5-2). */

import { useId, useState } from "react";

/*
 * 툴팁 — 🔴 **이 몰의 모든 버튼에 붙습니다** (오너 결정 2026-09-07 #4:
 * "버튼마다 툴팁 — 무슨 기능인지 알 수 있게 세심하게").
 *
 * 🔴 **말풍선은 마우스 hover 와 키보드 focus 에만 나옵니다.**
 *    종전에는 버튼 옆에 작은 `?` 를 달아 손가락으로도 열 수 있게 했는데, 실제 화면을
 *    본 오너가 **"오른쪽에 다 `?` 가 생겨서 이상하다"** 고 했습니다 (2026-09-07).
 *    맞는 지적입니다 — 이 몰의 큰 UI 원칙에서 주인공은 **버튼**이지 설명이 아닌데,
 *    버튼마다 물음표가 하나씩 따라붙으니 화면이 물음표 밭이 됐습니다.
 *    그래서 `?` 를 걷고, 설명은 **곁다리로 조용히** 되돌렸습니다.
 *
 * 🔑 터치 기기에는 hover 가 없으므로 말풍선이 아예 뜨지 않습니다 — 의도한 것입니다.
 *    그쪽에서 기능을 알리는 것은 **버튼에 적힌 글자**이고, 그 글자를 짧고 분명하게
 *    두는 것이 물음표를 다는 것보다 낫습니다.
 *
 * 🔴 **`aria-describedby` 로 화면 낭독기에는 그대로 갑니다.** 그래서 닫혀 있을 때도
 *    DOM 에 남깁니다(`sr-only`) — `display:none` 으로 숨기면 낭독기가 그 설명을
 *    읽지 못해, 눈으로 보는 사람만 아는 정보가 됩니다.
 */

export type TooltipTriggerProps = {
  "aria-describedby": string;
  onPointerEnter: (event: React.PointerEvent) => void;
  onPointerLeave: () => void;
  onFocus: (event: React.FocusEvent) => void;
  onBlur: () => void;
};

type Props = {
  /** 🔴 **무슨 일이 일어나는지** 한 문장으로. "이동합니다" 가 아니라 "어디로 갑니다" */
  text: string;
  /** 설명 상자를 컨트롤 위에 띄울지 아래에 띄울지. 화면 맨 위 컨트롤은 `bottom` */
  placement?: "top" | "bottom";
  /**
   * 말풍선의 어느 쪽 끝을 컨트롤에 맞출지. 🔑 **화면 오른쪽 끝에 붙은 컨트롤은
   * `right`** 입니다 — 기본값으로 두면 말풍선이 오른쪽으로 자라 화면 밖으로 나갑니다
   */
  align?: "left" | "right";
  children: (props: TooltipTriggerProps) => React.ReactNode;
  className?: string;
};

export function Tooltip({
  text,
  placement = "top",
  align = "left",
  children,
  className = "",
}: Props) {
  const id = useId();
  const [open, setOpen] = useState(false);

  const trigger: TooltipTriggerProps = {
    "aria-describedby": id,
    /* 🔴 **마우스일 때만**입니다. 손가락으로 누를 때도 `pointerenter` 는 오는데,
       그때 띄우면 누른 손가락 밑에서 말풍선이 뜬 채로 남습니다 — 치울 방법이
       없어서, `?` 를 걷어낸 자리에 같은 성가심이 그대로 돌아옵니다. */
    onPointerEnter: (event) => {
      if (event.pointerType === "mouse") setOpen(true);
    },
    onPointerLeave: () => setOpen(false),
    /* 🔴 **키보드로 옮겨온 초점에만**입니다. `:focus-visible` 로 가릅니다 —
       마우스·손가락으로 눌러서 생긴 초점까지 받으면, 누를 때마다 말풍선이
       한 번씩 번쩍이고 그것은 hover 로 보이지 않습니다. */
    onFocus: (event) => {
      if (event.currentTarget.matches(":focus-visible")) setOpen(true);
    },
    onBlur: () => setOpen(false),
  };

  return (
    <span className={`relative inline-flex ${className}`}>
      {children(trigger)}

      <span
        id={id}
        role="tooltip"
        className={
          open
            ? /* 🔴 **폭은 버튼을 넘지 않습니다** — `100%` 는 이 감싸개, 곧 버튼 폭입니다.
                 설명이 버튼보다 넓게 퍼지면 그것이 화면의 주인공처럼 보입니다.
               ⚠️ 다만 **12rem 은 바닥**입니다. 수량 ±, 밝기 전환처럼 글자 한 자짜리
                 버튼에서 폭을 그대로 따르면 한 글자씩 끊긴 60px 짜리 기둥이 되어
                 읽을 수가 없습니다. 종전 상한(22rem)의 절반이라 여전히 조용합니다.
               🔑 `pointer-events-none` — 아래로 띄운 말풍선이 마우스를 가로채면
                 커서가 버튼을 벗어난 것으로 처리돼 깜빡입니다. */
              `pointer-events-none absolute z-50 ${
                placement === "top" ? "bottom-full mb-2" : "top-full mt-2"
              } ${align === "right" ? "right-0" : "left-0"} w-max max-w-[max(100%,12rem)] rounded-[var(--tnf-radius-sm)] bg-ink px-3 py-2 text-caption leading-snug font-medium text-ground shadow-md`
            : "sr-only"
        }
      >
        {text}
      </span>
    </span>
  );
}
